<template>
  <div class="modal modal-filter-details fade" id="modal-add-talent-manager" role="dialog" aria-labelledby="shortlist-modal-label" aria-hidden="true" ref="modal">
    <div class="modal-dialog" v-click-outside="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title" id="shortlist-modal-label">
            <template >Apply For {{ $starLabel | sentenceCase }}</template>
          </h4>
        </div>
        <div class="modal-body">
          <form class="form-horizontal" @submit.prevent>
            <p v-if="this.tagNames">You will only be able to apply for {{ $starLabelPlural | sentenceCase }} with the following tags for this request: {{ this.tagNames }}</p>
            <label class="sr-only">Search for {{ $starLabelPlural | sentenceCase }}</label>
            <div class="input-group">
              <input
                type="text"
                class="form-control"
                :placeholder="$options.filters.sentenceCase($starLabel) + ' Name...'"
                v-model="search.name"/>

              <span class="input-group-btn">
                <ladda-button
                  ref="search"
                  class="btn btn-dark"
                  type="submit"
                  :click="handleSearchTalentCallback">
                  Search
                </ladda-button>
              </span>
            </div>

            <hr />

            <div class="text-center" v-if="haveNoTalentWithoutExisting && notSearching">
              <h5>There are no talent. Try searching to find who you want.</h5>

              <button v-if="haveSearchText" @click="handleClearSearchCallback" class="btn btn-default btn-sm">Clear Search</button>
            </div>
            <ul class="shortlist-modal-results" v-else>
              <li
                v-for="talent in talentWithoutExisting"
                :key="talent.id"
                class="row">
                <div class="col-xs-3 col-sm-2">
                  <img :src="talent.profile_image_url" alt="">
                </div>
                <div class="col-xs-6 col-sm-8">
                  <div class="details">
                    <p><strong>{{ talent.name }} <i class="fa" :class="{
                      'fa-lock': talent.profile.visibility === 'private',
                      'fa-eye-slash': talent.profile.visibility === 'hidden'
                    }"></i></strong></p>
                    <p>{{ talent.profile.tagline }}</p>
                    <span v-if="talent.date_applied_for" class="tag">Applied For {{ talent.date_applied_for | date($user.date_format_preference) }}</span>
                  </div>
                </div>
                <div v-if="booking.has_date_options_only" class="col-xs-3 col-sm-2 btn-group">
                  <a v-if="talentIsAlreadyAdded(talent)" @click="handleRemoveTalentCallback(talent)" class="btn btn-lg btn-success"><i class="fa fa-fw fa-check"></i></a>
                  <a v-else class="btn btn-lg btn-default" data-toggle="dropdown"  aria-haspopup="true" aria-expanded="false"><i class="fa fa-fw fa-plus"></i></a>

                  <ul class="dropdown-menu title" :id="'dropdown-menu-' + talent.id">
                    <li>Select a preferred date</li>
                    <li v-for="date in booking.date_options" :key="date">
                      <a class="hover" @click="handleAddTalentCallback(talent, date)">{{ date | date($user.date_format_preference) }}</a>
                    </li>
                  </ul>
                </div>
                <div v-else class="col-xs-3 col-sm-2">
                  <a v-if="talentIsAlreadyAdded(talent)" @click="handleRemoveTalentCallback(talent)" class="btn btn-lg btn-success"><i class="fa fa-fw fa-check"></i></a>
                  <a v-else class="btn btn-lg btn-default" @click="handleAddTalentCallback(talent)"><i class="fa fa-fw fa-plus"></i></a>
                </div>
              </li>
            </ul>
          </form>

          <lazy-loading
            scroll-target=".modal-body"
            class="text-center"
            :reached-bottom="() => { return getNextTalents(searchParams) }"
            ref="lazyLoading">
            <pickstar-loading slot="loading"/>
            <span slot="finished"></span>
          </lazy-loading>
        </div>
        <div class="modal-footer">
          <button ref="closeButton" type="button" class="btn btn-lg btn-danger pull-left" data-dismiss="modal"><i class="fa fa-close"></i> Cancel</button>
          <ladda-button
            type="button"
            class="btn btn-lg btn-primary"
            :click="handleSaveAdditionalTalentCallback">
            <i class="fa fa-check"></i> Save
          </ladda-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { filter, find, map, findIndex } from 'lodash'
import LazyLoading from 'codium-lazy-loading'
import $ from 'jquery'
import ClickOutside from 'vue-click-outside'

export default {
  data () {
    return {
      talentToAdd: [],
      searching: false,
      search: {
        name: null
      }
    }
  },

  components: {
    LazyLoading
  },

  directives: {
    ClickOutside
  },

  beforeDestroy () {
    this.closeModal()
  },

  computed: {
    ...mapState('booking/request', { booking: 'request' }),
    ...mapState('talent', ['talents']),

    tagNames () {
      return this.booking.tags.map(tag => tag.name).join(', ')
    },

    /**
     * Determine if we are not searching.
     *
     * @return bool
     */
    notSearching () {
      return !this.searching
    },

    /**
     * Determine if we have search text.
     *
     * @return bool
     */
    haveSearchText () {
      return this.search.name !== null
    },

    /**
     * Determine if we have no talent without the existing talent.
     *
     * @return bool
     */
    haveNoTalentWithoutExisting () {
      return this.talentWithoutExisting.length === 0
    },

    /**
     * Filter out the existing talent against the booking from the search results.
     *
     * @return array
     */
    talentWithoutExisting () {
      return filter(this.talents, (talent) => {
        return find(this.booking.talent, (existing) => existing.id === talent.id) === undefined
      })
    },

    searchParams () {
      return {
        status: 'active',
        booking_id: this.booking.id,
        keywords: this.search.name
      }
    }
  },

  mounted () {
    $(this.$el).on('hidden.bs.modal', () => {
      this.talentToAdd = []
      this.search.name = null
    })

    $(this.$el).on('show.bs.modal', () => {
      this.searching = true
      this.getTalents(this.searchParams).then(() => {
        this.searching = false
      })
    })
  },

  methods: {
    ...mapActions('talent', ['getTalents', 'getNextTalents']),
    ...mapActions('booking/request', ['addAdditionalBookingRequestTalent']),

    closeModal () {
      if (this.$refs.modal.classList.contains('in')) {
        this.$refs.closeButton.click()
      }
    },

    /**
     * Determine if a talent already exists within the additional talent array.
     *
     * @return bool
     */
    talentIsAlreadyAdded (talent) {
      return find(this.talentToAdd, added => added.id === talent.id) !== undefined
    },

    /**
     * Handle adding talent to the array of additional talent.
     *
     * @return boolean
     */
    handleAddTalentCallback (talent, date) {
      talent.date_applied_for = date
      this.talentToAdd.push(talent)
      $('#dropdown-menu-' + talent.id).toggle()
    },

    /**
     * Handle removing talent from the array of additional talent.
     *
     * @return void
     */
    handleRemoveTalentCallback (talent) {
      talent.date_applied_for = null
      this.talentToAdd.splice(findIndex(this.talentToAdd, existing => existing.id === talent.id), 1)
      $('#dropdown-menu-' + talent.id).toggle()
    },

    /**
     * Handle clearing the search.
     *
     * @return void
     */
    handleClearSearchCallback () {
      this.search.name = null

      this.$refs.search.$el.click()
    },

    /**
     * Handle searching the additional talent.
     *
     * Note: Need to emit reset lazy loader with new list in state.
     * @return Promise
     */
    handleSearchTalentCallback () {
      this.searching = true

      return this.getTalents(this.searchParams)
        .then(() => {
          this.searching = false
          this.$refs.lazyLoading.$emit('reset')
        })
    },

    /**
     * Save the selected additional talent against the booking as hidden responses.
     *
     * @return Promise
     */
    handleSaveAdditionalTalentCallback () {
      if (this.talentToAdd.length === 0) {
        return Promise.resolve()
      } else {
        const payload = {
          id: this.booking.id,
          payload: {
            talent: map(this.talentToAdd, talent => { return {'id': talent.id, 'date': talent.date_applied_for} })
          }
        }

        return this.addAdditionalBookingRequestTalent(payload)
          .then(() => {
            $(this.$el).modal('hide')
          })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.modal-body {
  overflow-y: scroll;
  max-height: 65vh;
}
</style>
